import type { IRowNode } from 'ag-grid-community';
import { useAtom } from 'jotai';
import { useCallback } from 'react';

import PERMISSIONS from '@/constants/auth.constants';
import { isReadOnlyManagedSourceType } from '@/features/SourceCreate/SourceCreate.constants';
import usePermissionsCheck from '@/hooks/usePermissionsCheck';
import { selectedOrganizationAtom } from '@/pages/Organizations';

import type { CloudSourceTreeNode } from '../CloudSourcesTable.types';

/**
 * Custom hook for managing row selection logic in cloud sources grid.
 *
 * Determines which rows can be selected based on:
 * - User has SOURCES_UPDATE permission
 * - Valid organization is selected
 * - Row is a source (not a provider group)
 * - Source has activation eligibility
 *
 * @returns Object containing:
 * - `canSelectRow` - Function to determine if a row is selectable
 * - `hasAdminUpdatePermission` - Whether user has SOURCES_UPDATE permission
 * - `selectedOrgData` - Currently selected organization data
 *
 * @example
 * ```tsx
 * const selection = useCloudSourcesRowSelection();
 * <AgGridReact
 *   isRowSelectable={selection.canSelectRow}
 * />
 * ```
 */
export const useCloudSourcesRowSelection = () => {
  const hasAdminUpdatePermission = usePermissionsCheck(
    PERMISSIONS.SOURCES_UPDATE
  );
  const [selectedOrgData] = useAtom(selectedOrganizationAtom);

  const canSelectRow = useCallback(
    (node: IRowNode<CloudSourceTreeNode>) =>
      hasAdminUpdatePermission &&
      !!selectedOrgData?.id &&
      node.data?.type === 'source' &&
      (node.data?.allowSourceActivation ?? false) &&
      !isReadOnlyManagedSourceType(node.data?.sourceData?.source_type_id),
    [hasAdminUpdatePermission, selectedOrgData?.id]
  );

  return {
    canSelectRow,
    hasAdminUpdatePermission,
    selectedOrgData
  };
};
